import { useEffect, useRef } from 'react';
import type { CanonicalQuestion } from '../../core/model/canonical.ts';
import type { Response } from '../../core/model/session.ts';
import type { SessionState } from '../../core/exam-engine/state.ts';
import type { SessionEvent } from '../../core/exam-engine/reducer.ts';
import { can, isStopped, progress } from '../../core/exam-engine/selectors.ts';
import { QUESTION_RENDERERS } from '../../questions/registry.tsx';
import { QuestionPrompt } from '../../questions/shared/QuestionParts.tsx';
import { useI18n } from '../../i18n/index.tsx';
import { Button } from '../../ui/primitives.tsx';

/**
 * The current question, and nothing else.
 *
 * Purely a way of looking at the session that is already running. It owns no state, starts nothing
 * and stops nothing: the same reducer, the same blueprint, the same renderer for the question type
 * — so an answer given in here is given to the session, and closing this puts the candidate back
 * exactly where they were, with the clock never having noticed.
 *
 * That is why it renders `QUESTION_RENDERERS[type]` rather than anything of its own. A second
 * implementation of "how a matching question is answered" would be a second place for it to be
 * wrong, and the two would drift the first time either was fixed.
 */
export function FocusOverlay({
  question,
  state,
  dispatch,
  onClose,
}: {
  readonly question: CanonicalQuestion;
  readonly state: SessionState;
  readonly dispatch: (event: SessionEvent) => void;
  readonly onClose: () => void;
}) {
  const { t } = useI18n();
  const counts = progress(state);
  const questionId = question.id;
  const entry = state.entries[questionId];
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  /**
   * Escape closes, and the page behind stops scrolling.
   *
   * The scroll lock is why this is an effect at all: the overlay covers the viewport, and without
   * it a scroll gesture that runs past the end of the question drags the exam around underneath.
   */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  /**
   * Focus moves in, and comes back out to where it was.
   *
   * Without the second half, closing would drop a keyboard user at the top of the document — which
   * is precisely the disorientation this screen exists to avoid.
   */
  useEffect(() => {
    const returnTo = document.activeElement;
    closeRef.current?.focus();

    return () => {
      if (returnTo instanceof HTMLElement && document.contains(returnTo)) {
        returnTo.focus();
      }
    };
  }, []);

  /** Keeps Tab inside the overlay, which is what `aria-modal` promises a screen reader. */
  const onKeyDownCapture = (event: React.KeyboardEvent): void => {
    if (event.key !== 'Tab' || panelRef.current === null) {
      return;
    }
    const focusable = [
      ...panelRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
      ),
    ];
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (first === undefined || last === undefined) {
      return;
    }

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const Renderer = QUESTION_RENDERERS[question.type];
  const paused = isStopped(state);

  return (
    <div className="focus" role="presentation">
      <div
        className="focus__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="focus-title"
        ref={panelRef}
        onKeyDown={onKeyDownCapture}
      >
        <div className="focus__bar">
          <p className="focus__name">{t('focus.title')}</p>
          <Button variant="quiet" ref={closeRef} onClick={onClose}>
            {t('focus.exit')}
          </Button>
        </div>

        <div className="focus__body">
          <h2 id="focus-title" className="focus__progress">
            {t('session.question', { position: counts.position, total: counts.total })}
          </h2>

          {/* A pause hides the questions everywhere, including here — otherwise this screen would
              be a way to read the exam with the clock stopped. */}
          {paused ? (
            <div className="paused">
              <h3>{t('session.paused')}</h3>
              <p>{t('session.pausedHelp')}</p>
              <Button
                variant="primary"
                onClick={() => {
                  dispatch({ type: 'RESUME' });
                }}
              >
                {t('session.resume')}
              </Button>
            </div>
          ) : (
            <article className="question">
              <QuestionPrompt question={question} />
              <Renderer
                question={question}
                optionOrder={state.blueprint.optionOrder[questionId]}
                response={entry?.response ?? null}
                onChange={(response: Response | null) => {
                  dispatch({ type: 'ANSWER', questionId, response });
                }}
                disabled={!can(state, 'ANSWER')}
                revealed={entry?.revealedAt !== null}
              />
            </article>
          )}
        </div>

        <div className="focus__controls">
          <Button
            disabled={!can(state, 'PREVIOUS')}
            onClick={() => {
              dispatch({ type: 'PREVIOUS' });
            }}
          >
            {t('session.previous')}
          </Button>

          {can(state, 'TOGGLE_MARK') && (
            <Button
              aria-pressed={entry?.markedForReview ?? false}
              onClick={() => {
                dispatch({ type: 'TOGGLE_MARK', questionId });
              }}
            >
              {entry?.markedForReview === true ? t('session.marked') : t('session.mark')}
            </Button>
          )}

          <Button
            variant="primary"
            disabled={!can(state, 'NEXT')}
            onClick={() => {
              dispatch({ type: 'NEXT' });
            }}
          >
            {t('session.next')}
          </Button>
        </div>
      </div>
    </div>
  );
}
